import { useEffect, useRef, type ReactNode } from "react";
import { Dismiss20Regular } from "@fluentui/react-icons";

interface ModalProps {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  maxWidthClass?: string;
}

export function Modal({ title, onClose, children, footer, maxWidthClass = "max-w-2xl" }: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  // Fechar no fundo só quando o gesto COMEÇA e ACABA no fundo — arrastar a seleção de texto de
  // um input para fora do painel dispara o click no fundo (alvo = ancestral comum) e fechava o
  // modal a meio da escrita.
  const backdropPressRef = useRef(false);
  // Keep the latest onClose without making the open effect depend on it — otherwise
  // an inline onClose changes identity every render, re-running the effect and
  // re-focusing the panel on EVERY keystroke (stealing focus from inputs).
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  // Mount-only: Esc para fechar, foco no painel ao abrir, e DEVOLVIDO a quem abriu ao fechar —
  // sem isto, fechar o modal largava o foco no <body> e um utilizador de teclado voltava ao topo
  // da página. O Tab fica preso dentro do painel enquanto o modal está aberto (focus trap): sem
  // ele, tabular a partir do último campo saía para a tabela por trás do diálogo.
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onCloseRef.current();
        return;
      }
      if (event.key !== "Tab") return;
      const panel = panelRef.current;
      if (!panel) return;
      const focusables = panel.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (focusables.length === 0) {
        event.preventDefault();
        panel.focus();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      if (event.shiftKey) {
        if (active === first || !panel.contains(active)) {
          event.preventDefault();
          last.focus();
        }
      } else if (active === last || !panel.contains(active)) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    panelRef.current?.focus();
    return () => {
      document.removeEventListener("keydown", onKey);
      opener?.focus?.();
    };
  }, []);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onMouseDown={(event) => {
        backdropPressRef.current = event.target === event.currentTarget;
      }}
      onClick={(event) => {
        if (backdropPressRef.current && event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className={`bg-card border border-border p-6 w-full ${maxWidthClass} space-y-4 overflow-y-auto outline-none`}
        style={{ borderRadius: "var(--radius-card)", maxHeight: "90vh" }}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3">
          <h3
            className="text-foreground"
            style={{ fontSize: "var(--text-section-title)", fontWeight: "var(--font-weight-semi-bold)" }}
          >
            {title}
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="p-2 hover:bg-accent transition-colors rounded min-h-[44px]"
            style={{ borderRadius: "var(--radius)" }}
          >
            <Dismiss20Regular className="w-5 h-5" />
          </button>
        </div>
        {children}
        {footer && <div className="flex justify-end gap-2 pt-2 flex-wrap">{footer}</div>}
      </div>
    </div>
  );
}
